import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Alert
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { supabase } from '../utils/supabaseClient';

const EditProfileForm = ({ open, onClose, profile, onProfileUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    headline: profile?.headline || '',
    location: profile?.location || '',
    company: profile?.company || '',
    website: profile?.website || '',
    github_url: profile?.github_url || '',
    twitter_url: profile?.twitter_url || '',
    linkedin_url: profile?.linkedin_url || '',
    bio: profile?.bio || ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = async (event) => {
    try {
      setError(null);
      const file = event.target.files[0];
      if (!file) return;

      if (!user?.id) {
        setError('Please sign in to upload an image');
        return;
      }

      setLoading(true);

      // First, ensure profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (!existingProfile) {
        // Create profile if it doesn't exist
        const { error: insertError } = await supabase
          .from('profiles')
          .insert([{ id: user.id }]);

        if (insertError) throw insertError;
      }

      // Then proceed with image upload
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `avatars/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      onProfileUpdate({ ...profile, avatar_url: publicUrl });
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!user?.id) {
        setError('Please sign in to update your profile');
        return;
      }

      setLoading(true);
      setError(null);

      // Create or update profile
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...formData,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      onProfileUpdate({ ...profile, ...formData });
      onClose();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add console log to debug
  console.log('Current user:', user);
  console.log('Current profile:', profile);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ textAlign: 'center', mb: 3, mt: 2 }}>
          <Avatar
            src={profile?.avatar_url}
            sx={{ width: 100, height: 100, margin: '0 auto', mb: 2 }}
          />
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="avatar-upload"
            type="file"
            onChange={handleImageUpload}
          />
          <label htmlFor="avatar-upload">
            <IconButton
              color="primary"
              component="span"
              disabled={loading}
            >
              <PhotoCamera />
            </IconButton>
          </label>
        </Box>

        <TextField
          fullWidth
          label="Full Name"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Headline"
          name="headline"
          value={formData.headline}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="GitHub URL"
          name="github_url"
          value={formData.github_url}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Twitter URL"
          name="twitter_url"
          value={formData.twitter_url}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="LinkedIn URL"
          name="linkedin_url"
          value={formData.linkedin_url}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Bio"
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          margin="normal"
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfileForm; 